import {toJS, computed, makeAutoObservable, action, configure,
        //autorun
       } from 'mobx'
import { socket, setConnectCallBack, setConnectErrCallBack } from './socket';
//import { persist } from 'mobx-persist';
import { makePersistable } from 'mobx-persist-store';
import { getLogger } from 'loglevel';


configure({
  enforceActions: "never",
});

const log = getLogger('FlowStore');
log.setLevel('info');

// Constants for app state
export const AppStates = {
  DASHBOARD: 1,
  REVIEW: 2,
  GENDOCS: 3
}

// Constants for ingesting
export const IngestStates = {
  NONE: 0,
  LOADING: 1,
  EXPLODING: 2,
  OCRING: 3,
  DONE: 4
}


export default class FlowStore {
  // **
  // These are all static config properties, should change only rarely if ever

  countryCodes = [];
  // We receive issue_code -> issue_name from server, but we break this down into
  // 'PAT'|'TM'|'PCT' -> { issue_code -> issue_name }
  issueTypes = {};
  // We receive doc_code -> doc_type_name from server
  docTypes = {};
  // We receive doc_code -> [attribute_names]+ from server
  docAttributes = {};
  // We receive attribute_name -> human_readable_string from server
  docAttributeNames = {};
  clientAttributeNames = {};
  attorneyAttributeNames = {};

  // **
  // Tenant and attorney specific properties
  
  availableAttorneys = {};
  attorney = '';
  availableClients = {};
  client = '';
  oldWorkspaces = [];
  workspace = '';

  // **
  // These properties are all specific to a particular matter

  matter = {}; // new Map(); //{};
  rejections = [];
  refMap = {};
  currentIssue = null;
  docType = null;
  matterType = 'PAT'; // PAT, TM, or PCT
  pages = [];
  images = [];
  numPages = null;
  templates = {};

  // These properties are related to tracking processing
  awaitingResults = false; 
  resultsURL = null;
  fetchRefEvents = [];
  ingestState = IngestStates.NONE;
  ocrPageCount = 0;

  refsDirty = false;
  appState = AppStates.DASHBOARD;
  
  version = window.VERSION;
  updateAvailable = false;
  currentUser = {};
  connectionState = 'Disconnected';
  percentComplete = 0;
  captchaPassed = false;
  isLoaded = false;

  testTemplateName = '';
  testTemplateText = null;
  templateResult = null;
  templateError = '';

  constructor() {
    setConnectCallBack((msg)=>{this.connectCallBack(msg)});
    setConnectErrCallBack((err)=>{this.connectErrCallBack(err)});
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'ptoflow',
      storage: window.localStorage,
      properties: ['countryCodes', 'issueTypes', 'docTypes', 'docAttributes', 'docAttributeNames',
                   'clientAttributes', 'clientAttributeNames',
                   'availableAttorneys', 'attorney', 'availableClients', 'client',
                   'oldWorkspaces', 'workspace', 'matter', 'rejections', 'refMap', 'currentIssue',
                   'docType', 'matterType', 'pages', 'images', 'numPages', 'templates',
                   'awaitingResults', 'resultsURL', 'fetchRefEvents', 'ingestState', 'ocrPageCount',
                   'refsDirty', 'appState', 'version', 'updateAvailable', 'currentUser']
    }).then((persistStore) => {
      console.log("Hydrated");
      this.isLoaded = true;
    });
  }

  js(obj) {
    return JSON.stringify(obj, null, 2);
  }

  connectCallBack(msg) {
    this.connectionState = 'LoggedIn';
    this.updateAvailable = this.version !== msg.version
      
    if (this.isAttorneySelected()) {
      log.info('Connect CB, setting atty', msg, this.attorney)
      socket.emit('attorneys', {workspace: '', op: 'select', id: this.attorney});
    }
  }

  connectErrCallBack(err) {
    log.info('Connect Error CB', JSON.stringify(err));
    if (err.description === 401) {
      this.connectionState = 'LoggedOut';
    }
    else {
      this.connectionState = 'Disconnected';
    }
  }

  // Returns true when a user has been defined
  haveUser() {
    return 'email' in this.currentUser;
  }

  isFreeDemo() {
    return this.currentUser['email'] === 'FreeDemoUser';
  }

  sessionInfo() {
    return {'User': this.currentUser['email'],
            'Tenant': this.currentUser['tenant'],
            'Atty':  this.attorney,
            'Workspace': this.workspace,
            'ConnectionState': this.connectionState,
            'Version': window.VERSION };
  }

  // This is close to a full reset, perform on logout to hide clients and
  // any other tenant-level information
  // Not reset:  generic application-level data, such as country codes, issue types, etc.
  tenantReset() {
    this.availableAttorneys = {};
    this.refresh();
  }
  
  // This resets everything except application config and tenant-level
  // data to the initial state.
  // Not reset:  available attorneys, clients, old workspaces, doctypes
  //  country codes, attribute names, etc.
  refresh() {
    log.info('FlowStore: refreshing session...');
    this.attorney = '';
    this.client = '';
    this.oldWorkspaces = [];
    this.docType = null;
    this.templates = {};
    this.resetWorkspace('');
    this.appState = AppStates.DASHBOARD;
    this.resetTemplateEditor();
  }

  // resets the workspace, but keeps the current attorney and their templates, etc
  resetWorkspace(ws) {
    log.info('FlowStore:  resetting workspace');
    this.workspace = ws;
    this.pages = [];
    this.images = [];
    this.numPages = null;   // used as a flag to see if no pages received yet, still exploding
    //this.templates = {};
    this.rejections = []
    this.refMap = {};
    this.currentIssue = null;
    this.matter = {}; // observable(new Map());
    this.matterType = 'PAT';

    this.awaitingResults = false;
    this.resultsURL = null;
    this.fetchRefEvents = [];
    this.ingestState = IngestStates.NONE;
    this.ocrPageCount = 0;
    this.refsDirty = false;
  }

  resetTemplateEditor() {
    this.testTemplateName = '';
    this.testTemplateText = null;
    this.templateResult = null;
    this.templateError = '';
  }

  debugInfo() {
    return {
      'version': this.version,
      'currentUser': this.currentUser,
      'connectionState': this.connectionState,
      'attorney': this.attorney, 'ws': this.workspace, 'client': this.client,
      'matter': this.matter, 'rejections': this.rejections, 'refMap': this.refMap,
      'numPages': this.numPages, 'templates': this.templates,
      'awaitingResults': this.awaitingResults, 'resultsURL': this.resultsURL,
      'ingestState': this.ingestState, 'ocrPageCount': this.ocrPageCount,
      'appState': this.appState
    }
  }

  // predicates
  isAttorneySelected() { return this.attorney !== '' }
  isClientSelected() { return this.client !== '' }
  isWorkspaceSelected() { return this.workspace !== '' }
  haveMatterInfo() { return Object.keys(this.matter).length !== 0 }
  //isOcrComplete() { return this.numPages === this.ocrPageCount }

  getFetchRefEvents() { return this.fetchRefEvents.slice();  }

  getTemplates() {
    let dt = this.docType;
    if (dt === null)
      return [];
    if (!(dt in this.templates)) {
      let parts = dt.split('.')
      if (parts.length > 1)
        dt = parts[0] + '.*';
      if (!(dt in this.templates))
        dt = '_default';
    }
    if (dt in this.templates)
      return this.templates[dt].slice();
    else
      return [];
  }

  getDocAttributes(dt) {
    if (!dt) return ['num', 'app_num'];
    let attributes = this.docAttributes[dt];
    if (!attributes && dt.includes('.')) { // check for wildcards
      dt = dt.split('.')[0] + '.*';
      attributes = this.docAttributes[dt];
      if (!attributes)
        attributes = this.docAttributes["*"];
      if (!attributes)
        attributes = ['num', 'app_num']
    }
    return attributes;
  }
  
  // Sets up a number of handlers
  // attorneys:  every time a list response is received, the attorney list is updated
  //             every time a new attorney is selected, current atty is updated
  // workspaces:  every time a list response is received, oldWorkspaces is updated
  // config:  calls and updates the config info

  init() {
    console.log("Initializing store");
    socket.on('attorneys_response', (msg) => {
      //log.info("FlowStore: Received atty response" + JSON.stringify(msg, null, 2));
      this.reportErrors('attorneys_response', msg);
      if (msg.op === 'list') {
        this.addAttorneys(msg.data.data);
        // This is here to handle cases were the user removes the working attorney
        // via the admin interface.  
        if (!(this.attorney in this.availableAttorneys)) {
          log.warn('FlowStore: Attorney has gone missing, changing to FirmShared');
          this.setAttorney('FirmShared');
        }
      }
      else if (msg.op === 'select') {
        if (this.attorney !== msg.data)
          this.attorney = msg.data;
      }
    });
    socket.emit('attorneys', {workspace: '', op: 'list'});

    socket.on('config_response', (msg) => {
      //log.info('Received config: ' + JSON.stringify(msg, null, 2));
      this.reportErrors('config_response', msg);
      let config = msg.config
      this.countryCodes = Object.keys(config.country_codes);
      this.issueTypes = {'PAT': {}, 'TM': {}, 'PCT': {}}
      for (let k in config.issue_types) {
	if (k.startsWith('tm.'))
	  this.issueTypes['TM'][k] = config.issue_types[k];
	else if (k.startsWith('pct.'))
          this.issueTypes['PCT'][k] = config.issue_types[k];
        else
	  this.issueTypes['PAT'][k] = config.issue_types[k];
      };
      //log.info(toJS(this.issueTypes));
      this.docTypes = config.doc_types;
      this.docAttributes = config.doc_attributes;
      this.docAttributeNames = config.doc_attribute_names;
      this.clientAttributeNames = config.client_attribute_names;
      this.attorneyAttributeNames = config.attorney_attribute_names;
    });
    socket.emit('config', {workspace: ''});

    socket.on('document_response', (msg) => {
      this.reportErrors('document_response', msg);
      if (msg.op === 'get_pages') {
        //log.info('Received pages ' + JSON.stringify(msg, null, 2));
        log.info('FlowStore:  Received pages ' + msg.pages.length);
        this.pages = msg.pages;
        this.numPages = msg.pages.length;
        this.loadImages();
      }
      else if (msg.op === 'get_matter') {
        log.info('FlowStore: Received matter ' + JSON.stringify(msg.matter, null, 2));
        //this.matter = msg.matter;
        this.updateMatter(msg.matter);
      }
      else if (msg.op === 'process') {
        //log.info('Received process result ' + JSON.stringify(msg, null, 2));
        //this.matter = msg.matter;
        this.updateMatter(msg.matter);
        this.docType = msg.doc_type.doc_type;
        this.matterType = this.docType.split('.')[0]
        this.rejections = msg.issues;
      }
    });

    socket.on('get_image_response', (msg) => {
      //log.info('FlowStore: Received image url');
      this.reportErrors('get_image_response', msg);
      this.images.push({url: msg.url, image_num: msg.image_num});
    });

    socket.on('clients_response', (msg) => {
      log.info('FlowStore: clients_response', msg);
      this.reportErrors('clients_reponse', msg);
      if (msg.op === 'list') {
        this.addClients(msg.data.data); // response format is {data: {data: list, total: n}}
        log.info('FlowStore: added clients');
        // This is here to handle cases were the user removes the current client
        // via the admin interface.  
        if (!(this.client in this.availableClients))
          this.setClient('Default');
      }
    }); 
    socket.emit('clients', {workspace: '', op: 'list'}); 
    
    socket.on('users_response', (msg) => {
      this.reportErrors('users_reponse', msg);
      if (msg.op === 'current') {
        this.currentUser = msg.data;
      }
    });
    socket.emit('users', {workspace: '', op: 'current'}); 

    socket.on('status_response', (msg) => {
      log.info('FlowStore: server_status',  msg);
      if (this.version !== msg.version) {
        this.updateAvailable = true;
      }
    });
    //this.pollServerStatus();
    
    //socket.on('matter_response', (msg) => {
    //  log.info('Matter response', msg)
    //});
    //socket.emit('workspaces', {workspace: '123', op: 'list'});

    //socket.on('get_image_response', (msg) => {
    //  log.info("Received IMG Info" + JSON.stringify(msg, null, 2));
    //  this.props.store.images.push(msg.url);
    //});
    //socket.emit('get_image', {workspace: '340a09f1a06bc03e', image_num: 1});

  }

  reportErrors(name, msg) {
    if (msg.status !== 'OK') {
      log.warn('FlowStore: Error in ' + name, JSON.stringify(msg, null, 2));
      return true;
    }
    return false;
  }

  // ----------------------------------------------------------------------------
  // Attorney related

  // takes an attorney ID, sets the current attorney to ID and flushes to server
  // also lists the attorney's workspaces and templates
  setAttorney(atty) {
    log.info('FlowStore:  Setting atty: ' + atty);
    if (this.atty !== atty) {
      this.attorney = atty;
      this.workspace = '';
      socket.emit('attorneys', {workspace: '', op: 'select', id: atty});
      this.listWorkspaces();
      this.listTemplates();
    }
  }

  // gets the atty struct for the given ID
  getAttorney(atty) {
    if (atty in this.availableAttorneys) {
      return this.availableAttorneys[atty];
    }
    return null;
  }

  // takes an attorney dict
  updateAttorneys(atty) {
    //this.attorney = atty.id;
    this.availableAttorneys[atty.id] = atty;
    socket.emit('attorneys',  { op: 'add', data:atty });
  }

  // takes an attorney ID and updates server (by default)
  // Refuses to delete FirmShared
  deleteAttorney(atty, sync=true) {
    if (atty === 'FirmShared')
      return;
    delete this.availableAttorneys[atty];
    if (this.attorney === atty)
      this.setAttorney('FirmShared');
    if (sync)
      socket.emit('attorneys', { op: 'delete', id:atty });
  }

  // adds a list of attorneys, clears the dict first
  addAttorneys(attys) {
    this.availableAttorneys = {};
    attys.map((a, idx) => { return this.availableAttorneys[a.id] = a });
  }

  // ----------------------------------------------------------------------------
  // Client related

  // takes Client ID and updates server
  setClient(client) {
    log.info('FlowStore: Setting client: ' + client);
    this.client = client;
    socket.emit('clients', {workspace: this.workspace, op: 'select', id: client});
  }

  // takes an client dict and updates server
  updateClients(client) {
    this.availableClients[client.id] = client;
    socket.emit('clients',  { op: 'add', data:client });
  }

  // takes Client ID and updates server (default)
  // Refuses to delete Default client
  deleteClient(client, sync=true) {
    if (client === 'Default')
      return
    delete this.availableClients[client];
    if (this.client === client)
      this.setClient('Default');
    if(sync)
      socket.emit('clients', {workspace: this.workspace, op: 'delete', id:client});
  }

  // adds a list of clients, clears the dict first
  addClients(clients) {
    this.availableClients = {};
    clients.map((a, idx) => { return this.availableClients[a.id] = a });
  }

  // ----------------------------------------------------------------------------
  // Matter related
  
  // takes a matter dict
  // this is imperfect.  We don't want to just replace the old matter structure, because
  // if it is incomplete (e.g., no notification_date) then we lose those fields.
  updateMatter(matter) {
    //log.info('Updating matter', toJS(this.matter));
    //log.info('  with', matter);
    if (Object.keys(this.matter).length === 0) {
      this.matter = matter;
    }
    else {
      for (let k in this.matter) {
        this.matter[k] = matter[k] || '';
      }
    }
    // It's really important to have notification dates, even if they are wrong!
    if (this.matter['notification_date'] === '') {
      this.matter['notification_date'] = todaysDate();
    }
  }

  lookupMatter(app_num) {
    let listener = (msg) => {
      log.info('FlowStore: Matter response', msg);
      socket.off('matter_response', listener);
      if (msg.data && Object.keys(msg.data).length) {
        this.updateMatter(msg.data);
        //for (let k in msg.data)
        //  this.matter[k] = msg.data[k];
      }
    };
    socket.on('matter_response', listener);
    socket.emit('matter', { workspace: this.workspace, op: 'lookup',
                            app_num: app_num });
  }

  // sends the list workspaces message
  listTemplates() {
    let listener = (msg) => {
      socket.off('templates_response', listener);
      if (this.reportErrors('templates_response', msg)) {
        log.warn('FlowStore: Missing templates, continuing ...');
        return
      }
      if (msg.op === 'list') {
        log.info('FlowStore: Received templates', msg);
        this.templates = msg.templates;
      }
    };
    socket.on('templates_response', listener);
    socket.emit('templates', { workspace: '', op: 'list' });
  }


  // ----------------------------------------------------------------------------
  // Workspace related
  
  // sends the list workspaces message
  listWorkspaces() {
    if (this.attorney !== '') {
      log.info('FlowStore: listing workspaces');
      let listener = (msg) => {
        //log.info("Received WS Info" + JSON.stringify(msg, null, 2));
        socket.off('workspaces_response', listener)
        if (msg.op === 'list') {
          // first remove any bad workspaces
          // remove test workspace 0000
          let workspaces = msg.data.filter(a => a.matter && a.doctype && a.ws !== '0000');
          workspaces.sort((a,b) => { return a.mtime > b.mtime });
          this.oldWorkspaces.length = 0;
          this.oldWorkspaces.push(...workspaces);
        }
      };
      socket.on('workspaces_response', listener);
      socket.emit('workspaces', {workspace: '', op: 'list'});
    }
  } 

  loadWorkSpace(ws) {
    this.resetWorkspace(ws);
    // make sure the server knows who the attorney is
    if (this.attorney !== '')
      socket.emit('attorneys', {workspace: ws, op: 'select', id: this.attorney});
    // get pages
    //socket.emit('document', { workspace: this.workspace, op: 'get_matter' });
    socket.emit('document', { workspace: this.workspace, op: 'get_pages' });
    socket.emit('document', { workspace: this.workspace, op: 'process' });
    this.listTemplates();

    //socket.emit(
    // load images
    // process OA
    // get templates
  }

  loadImages() {
    for (let i = 1; i <= this.numPages; i++) {
      socket.emit('get_image', { workspace: this.workspace, image_num: i });
    }
  }

  // deletes the current workspace and then refreshes the list from the server
  async deleteWorkspace() {
    let doDelete = () => {
      return new Promise((resolve, reject) => {
        let listener = (msg) => {
          socket.off('workspaces_response', listener);
          resolve();
          log.info('FlowStore: Deleted WS: ' + msg + ' ' + msg.status)
        };
        socket.on('workspaces_response', listener);
        log.info('FlowStore: Deleting: ' + this.workspace);
        socket.emit('workspaces', { workspace:this.workspace, op: 'delete' });        
      });
    }
    await this.timeout(10000, doDelete());
    this.listWorkspaces();
  };
  
  // creates a new workspace on server
  async createWorkspace() {
    let doCreate = () => {
      return new Promise((resolve, reject) => {
        let listener = (msg) => {
          socket.off('workspaces_response', listener);
          resolve();
          log.info('FlowStore: Created WS: ' + msg + ' ' + msg.status)
        };
        socket.on('workspaces_response', listener);
        log.info('FlowStore: Creating: ' + this.workspace);
        socket.emit('workspaces', { workspace:this.workspace, op: 'create' });        
      });
    }
    await this.timeout(10000, doCreate());
    this.listWorkspaces();
  };

  getWorkspace(ws) {
    if (ws) {
      for (let i=0; i<this.oldWorkspaces.length; i++) {
        if (this.oldWorkspaces[i].ws === ws) {
          return this.oldWorkspaces[i];
        }
      }
    }
    return null;
  }

  // ----------------------------------------------------------------------------
  // Issues related
  
  nextIssue() {
    if (this.currentIssue === null && this.rejections.length > 0) {
      this.currentIssue = 0;
    }
    else if (this.currentIssue !== this.rejections.length - 1) {
      this.currentIssue += 1;
    }
  }

  prevIssue() {
    if (this.currentIssue === 0 || this.rejections.length === 0) {
      this.currentIssue = null;
    }
    else if (this.currentIssue !== null) {
      this.currentIssue -= 1;
    }
  }

  setCurrentIssue(index) {
    this.currentIssue = index;
  }

  // removes given issueNum, keeps the currentIssue at its location
  // unless there are no more issues or we have removed the last issue
  removeIssue(issueNum) {
    if (issueNum < 0 || issueNum >= this.rejections.length)
      return;
    this.rejections.splice(issueNum, 1);
    if (this.rejections.length === 0)
      this.currentIssue = null;
    else if (this.currentIssue >= this.rejections.length)
      this.currentIssue = this.rejections.length - 1
  }

  async addIssue(lineNum, pageNum) {
    let guessIssue = () => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          log.info(msg);
          // insert sorted here
          let inserted = false
          let issues = this.rejections
          for (let i=0; i<issues.length; i++) {
            if ((issues[i].page_num === pageNum && issues[i].line_num > lineNum) ||
                issues[i].page_num > pageNum) {
              issues.splice(i, 0, msg.issue);
              inserted = true;
              this.currentIssue = i;
              break;
            }
          }
          // We didn't insert so we go at the end.
          if (!inserted) {
            issues.splice(issues.length, 0, msg.issue);
            this.currentIssue = issues.length-1
          }
          socket.off('guess_issue_response', listener);
          resolve();
        };
        socket.on('guess_issue_response', listener);
        socket.emit('guess_issue',
                    {workspace:this.workspace, line_num:lineNum, page_num:pageNum});
      });
    };
    await this.timeout(10000, guessIssue());
  }

  // ----------------------------------------------------------------------------
  // Reference management

  hasReferences() {
    for (let rejection of this.rejections) {
      if (rejection.references.length !== 0)
        return true;
    }
    return false;
  }
  
  removeReference(refNum) {
    this.refsDirty = true;
    let rejection = this.rejections[this.currentIssue];
    if (refNum < 0 || refNum >= rejection.references.length)
      return;
    rejection.references.splice(refNum, 1);
  }

  addReference() {
    this.refsDirty = true;
    let rejection = this.rejections[this.currentIssue];
    rejection.references.push({num:'', country:'US', type:'pat', alias:''})
  }

  // updates current issue
  updateReference(refNum, key, val) {
    this.refsDirty = true;
    let rejection = this.rejections[this.currentIssue];
    if (refNum < 0 || refNum >= rejection.references.length)
      return;
    rejection.references[refNum][key] = val;
  }

  // Pushes all of the current issue's info into other issues
  // should only call this when finished editing the issue.
  // Current strategy is to keep a reference map (num/alias -> reference)
  // Every time an issue is confirmed, the reference map is used to
  // first fill in any holes.  Then all the references are added to the
  // map, then the map is used to correct references throughout the
  // issue list.  
  propagateRefInfo() {
    if (this.currentIssue === null || this.rejections.length < this.currentIssue)
      return;
    let rejection = this.rejections[this.currentIssue];
    log.info('FlowStore: Updating reference info', toJS(rejection));
   
    for (let ref of rejection.references) {
      // clean up weird characters
      ref.alias = ref.alias.trim();
      ref.num = ref.num.trim().replace(/\D/g, '');
      // First we update any in the current rejection based on the refMap
      if (ref.alias === '' && this.refMap[ref.num])
        ref.alias = this.refMap[ref.num].alias;
      else if (ref.num === '' && this.refMap[ref.alias])
        ref.num = this.refMap[ref.alias].num;
      // Now update the refMap
      if (ref.alias !== '' && ref.num !== '') {
	this.refMap[ref.alias] = ref;
	this.refMap[ref.num] = ref;
      }
    }
    //log.info('FlowStore: refmap', toJS(this.refMap))
    for (let issue of this.rejections) {
      for (let ref of issue.references) {
	if (ref.alias in this.refMap) {
	  ref.num = this.refMap[ref.alias].num;
	  ref.country = this.refMap[ref.alias].country;
	  ref.type = this.refMap[ref.alias].type;
	}
	if (ref.num in this.refMap) {
	  ref.alias = this.refMap[ref.num].alias;
	  ref.country = this.refMap[ref.num].country;
	  ref.type = this.refMap[ref.num].type
	}
	// Fix every US reference... to its appropriate type
	if (ref.country === 'US') {
	  if (ref.num.length === 11)
	    ref.type = 'app';
	  else if (ref.num.length <= 8)
	    ref.type = 'pat';
	}
      }
    }
    this.refsDirty = false;
  }
  
  // ----------------------------------------------------------------------------
  // Sync related

  // If timeout (ms) is 0, then just executes given promise
  async timeout(ms, promise) {
    return new Promise((resolve, reject) => {
      if (ms > 0) {
        setTimeout(() => {
          reject(new Error('time out'));
        }, ms);
      }
      promise.then(resolve, reject);
    }).catch(() => { console.error('Timeout!!!')});
  };

  // Update the information associated with a given document
  // Documents include matter, issues, doc_type, attorneys, clients, users(?)
  async updateInfo(workspace, doc, data, timeout) {
    let update = () => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          socket.off('update_response', listener);
          resolve(msg.workspace);
          log.info('FlowStore: Updated: ' + doc + ' ' + msg.status)
        };
        socket.on('update_response', listener);
        log.info('FlowStore: Updating: ' + doc + ' ' + this.js(data));
        socket.emit('update', { workspace, doc, data });        
      });
    }
    //if (timeout > 0)
      await this.timeout(timeout, update());
    //else
    //  update();
  }

  // This will initiate a reprocess at the server end by default
  setDocType(docType, reprocess=true) {
    this.docType = docType;
    this.matterType = this.docType.split('.')[0]
    this.updateInfo(this.workspace, 'doc_type',  { doc_type: docType, confidence: 1.0 }, 10000 );
    if (reprocess)
      socket.emit('document', { workspace: this.workspace, op: 'process' });
  }

  // Write back the current matter and issues to the server
  // FIXME:  updateInfo already blocks to be sure that the data is written
  // We should just call it directly, with a zero timeout if we don't care...
  async saveState(timeout) {
    log.info('FlowStore: Saving state to server');

    if (this.isWorkspaceSelected() && this.haveMatterInfo())
      this.updateInfo(this.workspace, 'matter', this.matter, timeout);

    if (this.isWorkspaceSelected())
      this.updateInfo(this.workspace, 'issues', this.rejections, timeout);
  }

  sleep(ms) {
    const date = Date.now();
    let currentDate = null;
    do {
      currentDate = Date.now();
    } while (currentDate - date < ms);
  }

  // ----------------------------------------------------------------------------
  // Upload/Process new file input

  // Uploads test template
  // Use workspace 0000
  async ingestTestTemplate(file) {

    //this.resetWorkspace('');
    this.testTemplateName = 'test_' + file.name;
    
    let getArrayBufferFromFile = file =>  {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result);
        };
        reader.onabort = () => reject(new Error('file reading was aborted'));
        reader.onerror = () => reject(new Error('file reading has failed'));
        //reader.readAsArrayBuffer(file);
        reader.readAsText(file);
      });
    };
    
    try {
      // make sure the server knows who the attorney is
      if (this.attorney !== '')
        socket.emit('attorneys', {workspace: this.workspace, op: 'select', id: this.attorney});

      const defaultTimeout = 10000; 
      this.testTemplateText =
            await this.timeout(defaultTimeout, getArrayBufferFromFile(file));
    }
    catch (err) {
      socket.off();
      log.warn('FlowStore: Error loading template file: ' + err);
    }
  }

  // Load a template from server
  async loadTemplateFromServer(name) {

    this.testTemplateName = name;
    
    let fetchTemplate = () => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          log.info('FlowStore: Load template response: ', msg);
          this.testTemplateText = msg.data;
          if (msg.status === 'NOT_FOUND')
            this.testTemplateText = 'No template found, write new one here...';
          socket.off('templates_response', listener);
          resolve(msg.workspace);
        };
        socket.on('templates_response', listener);
        socket.emit('templates', {workspace:'0000', op: 'get', name:name});
      });
    };
    
    try {
      // make sure the server knows who the attorney is
      if (this.attorney !== '')
        socket.emit('attorneys', {workspace: this.workspace, op: 'select', id: this.attorney});

      const defaultTimeout = 10000; 
      await this.timeout(defaultTimeout, fetchTemplate());
    }
    catch (err) {
      socket.off();
      log.warn('FlowStore: Error loading template file: ' + err);
    }
  }

  async applyTestTemplate(docType, templateText) {
    console.log('Apply', templateText)
    let templates = [this.testTemplateName];
    this.testTemplateText = templateText;
    this.workspace = '0000';
    this.templateError = '';
    this.templateResult = '';
    this.createWorkspace();
    this.updateInfo(this.workspace, 'matter', exampleData.matter, 10000);
    this.updateInfo(this.workspace, 'issues', exampleData.issues, 10000);
    this.updateInfo(this.workspace, 'doc_type',  { doc_type: docType, confidence: 1.0 }, 10000);
    // Delete old results
    socket.emit('results', { workspace: this.workspace, op: 'delete' });
    // Upload the working template
    let uploadTemplate = (fileName, text) => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          log.info('FlowStore: Upload template response: ', msg);
          socket.off('templates_response', listener);
          resolve(msg.workspace);
        };
        socket.on('templates_response', listener);
        var enc = new TextEncoder();
        socket.emit('templates', {workspace:'0000', op: 'upload', data:enc.encode(text), name:fileName});
      });
    };
    let createDocs = () => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          log.info('FlowStore: getResults templates_response ', msg);
          socket.off('templates_response', listener);
          if (msg.status === 'TEMPLATES_ERR') {
            this.templateError = msg.error;
          }
          resolve(msg.workspace);
        };
        socket.on('templates_response', listener);
        socket.emit('templates', { workspace: this.workspace, op: 'apply', templates });
      });
    };

    let fetchResult =() => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          log.info('FlowStore: getResults get_one response ', msg);
          socket.off('results_response', listener);
          this.templateResult = msg.doc;
          resolve(msg.workspace);
        };
        socket.on('results_response', listener);
        socket.emit('results', { workspace: this.workspace, op: 'get_one', filename:this.testTemplateName });
      });
    };

    this.setClient(this.client);
    let defaultTimeout = 10000;
    await this.timeout(defaultTimeout, uploadTemplate(this.testTemplateName, this.testTemplateText));
    await this.timeout(defaultTimeout, createDocs());
    await this.timeout(defaultTimeout, fetchResult());
  }


  

  // Uses the upload2 experimental socket command to send chunks of the fileBuffer
  // to the server.  After each chunk is uploaded, percentage completion is
  // updated.
  async uploadBuffer(fileName, fileBuffer) {
    this.percentComplete = 0;
    
    let uploadChunk = (slice, fileName, index, workspace) => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          console.log('Chunk', msg.workspace, msg.status, msg.chunk);
          socket.off('upload2_response', listener);
          resolve(msg.workspace);
        };
        socket.on('upload2_response', listener);
        console.log('Sending', index, workspace);
        socket.emit('upload2', {file:slice, name:fileName, chunk:index, workspace:workspace});
      });
    };
    
    var length = fileBuffer.byteLength;
    var chunk = 0;
    var index = 0;
    var workspace = 0;
    var CHUNK_SIZE = 128 * 1024;
    console.log('Uploading', length);
    while (index < length) {
      let slice = fileBuffer.slice(index, Math.min(index+CHUNK_SIZE, length));
      workspace = await this.timeout(0, uploadChunk(slice, fileName, chunk, workspace));
      index += CHUNK_SIZE;
      chunk += 1;
      this.percentComplete = Math.min(1.0, index / length);
      console.log(this.percentComplete);
    }
    return workspace;
  }
    
  // Uploads file and asks server to process it
  async ingestFile2(file) {

    this.resetWorkspace('');
    this.ingestState = IngestStates.LOADING;

    let getArrayBufferFromFile = file =>  {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result);
        };
        reader.onabort = () => reject(new Error('file reading was aborted'));
        reader.onerror = () => reject(new Error('file reading has failed'));
        reader.readAsArrayBuffer(file);
      });
    };

    let watchOCRProgress = () => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          log.debug(msg);
          switch (msg.event.name) {
          case 'EXPLODE_PDF_START':
            log.info('Explode start');
            this.ingestState = IngestStates.EXPLODING;
            break;
          case 'EXPLODE_PDF_END':
            log.info('Explode end');
            this.numPages = parseInt(msg.event.num_pages, 10);
            this.ingestState = IngestStates.OCRING;
            break;
          case 'EXPLODE_PDF_ERROR':
            log.info('Explode error');
            this.ingestState = IngestStates.DONE; // fixme:  should implement error handling...
            break;
          case 'OCR_PAGE_DONE':
            this.ocrPageCount += 1;
            break;
          case 'OCR_END':
            socket.off('ocr_event', listener);
            this.ingestState = IngestStates.DONE;
            this.appState = AppStates.REVIEW;
            resolve();
            break;
          default:
            break;
          }
        };
        socket.on('ocr_event', listener);
      });
    };

    try {
      // make sure the server knows who the attorney is
      if (this.attorney !== '')
        socket.emit('attorneys', {workspace: this.workspace, op: 'select', id: this.attorney});

      const defaultTimeout = 90000; // it can take a while to upload large files.
      const fileAsArrayBuffer =
            await this.timeout(defaultTimeout, getArrayBufferFromFile(file));
      this.workspace =
        await this.timeout(defaultTimeout, this.uploadBuffer(file.name, fileAsArrayBuffer));
      
      socket.emit('document', { workspace: this.workspace, op: 'ocr' });
      await watchOCRProgress();

      socket.emit('document', { workspace: this.workspace, op: 'get_pages' });
      socket.emit('document', { workspace: this.workspace, op: 'process' });
      this.listTemplates();

    }
    catch (err) {
      socket.off();
      log.warn('FlowStore: Error ingesting file: ' + err);
    }
  }

  async getResults(fetchRefs, templates) {
    // FIX:  Here we update matter info, update issues, and then fetch references
    //  updateInfo(this.workspace, 'matter', this.matter);
    //  updateInfo(this.workspace, 'issues', this.rejections);
    //  socket.emit('fetch_refs', { workspace });
    // create documents
    // and then send a socket.emit('results', { workspace, op: 'get' });
    // Although we really should decide whether the changes get made as we go or at the end...

    this.awaitingResults = true;
    this.fetchRefEvents = [];

    let fetchReferences = () => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          log.info('FlowStore: Fetch_ref_event: ' + JSON.stringify(msg));
          if (msg.results) {
            socket.off('fetch_ref_event', listener);
	    log.info('FlowStore: Finished getting references');
            resolve();
          }
          else if (msg.event) {
            this.fetchRefEvents.push(msg.event);
	    // If we resolve() here, then the we will have a race condition
          }
        };
        socket.on('fetch_ref_event', listener);
        socket.emit('fetch_refs', { workspace: this.workspace });
      });
    };

    let createDocs = () => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          log.info('FlowStore: getResults templates_response ', msg);
          // FIXME:  we are just punting here on the first response we get.
          // We should probably collect all of them, but this works for now, since
          // we do not provide any feedback to the user about the templating process
          socket.off('templates_response', listener);
          resolve();
        };
        socket.on('templates_response', listener);
        socket.emit('templates', { workspace: this.workspace, op: 'apply', templates });
      });
    };

    let bundleResults =() => {
      return new Promise((resolve, reject) => {
        let listener = msg => {
          log.info(msg);
          if (msg.url) {
            socket.off('results_response', listener);
            log.info('FlowStore: Received results: ' + msg.url);
            this.resultsURL = msg.url;
            this.awaitingResults = false;
            resolve();
          }
        };
        socket.on('results_response', listener);
        socket.emit('results', { workspace: this.workspace, op: 'get' });
      });
    };

    this.setClient(this.client);
    let defaultTimeout = 10000;
    this.saveState(defaultTimeout);
    if (fetchRefs)
      await this.timeout(defaultTimeout*3, fetchReferences());
    if (templates)
      await this.timeout(defaultTimeout, createDocs());
    await this.timeout(defaultTimeout, bundleResults());

    // Once we have made the bundle, delete the results, but ONLY if
    // we are in Free Demo mode, because the user may have created garbage
    if (this.isFreeDemo())
      socket.emit('results', { workspace: this.workspace, op: 'delete' });
  }

  resetGettingResults() {
    this.fetchRefEvents = [];
    this.awatingResults = false;
    this.resultsURL = null;
  }

  test() {
    log.info('FlowStore: Running test');
    let listener = msg => {
      log.info('FlowStore: Received ping response', msg);
      socket.off('_ping_response', listener);
    }
    socket.on('_ping_response', listener);
    socket.emit('_ping', { workspace: this.workspace, op: '' });
  }

  pollServerStatus() {
    socket.emit('status', {user: this.currentUser});
    setTimeout(() => this.pollServerStatus(), 60000);
  }


  // ----------------------------------------------------------------------------
  // Computed properties 

  // Returns a label pair for given docType
  docTypeLabel(dt) {
    if (dt === null) return '';
    let tag = '(Pat)';
    if (dt.startsWith('PCT')) {
      tag = '(PCT)';
    }
    else if (dt.startsWith('TM')) {
      tag = '(TM)';
    }
    return {value: dt, label: this.docTypes[dt] + ' ' + tag}
  }

  // Returns label based on current type
  @computed get currentDocTypeLabel() {
    return this.docTypeLabel(this.docType);
  }

  // Returns doctype list {value: doc_type, label: human_readable }
  @computed get docTypeLabels() {
    let items = Object.keys(this.docTypes).map((docType) => {
      return this.docTypeLabel(docType)
    }).sort((a,b) => a.value.substring(0,2) + a.label > b.value.substring(0,2) + b.label ? 1 : -1);
    //log.info(items);
    return items;
  }

  @computed get clientLabels() {
    let items = Object.keys(this.availableClients).map((k) => {
      return {value: k, label: k + ': ' + this.availableClients[k].name};
    });
    items = items.sort((a,b) => a.label > b.label ? 1 : -1);
    log.info('FlowStore: labels ', items);
    return items;
  }

  @computed get countryCodeOptions() {
    let items = this.countryCodes.reduce(
      (list, cc) => {
        return [...list, `${cc} pat`, `${cc} app`];
      }, []).concat(['__ npl', '__ other']);
    return items;
  }

  clientAttr(fieldName) {
    let c = this.availableClients[this.client];
    if (c) {
      return c[fieldName];
    }
    else {
      return ''
    }
  }
};
 
const todaysDate = () => {
  var today = new Date();
  var dd = today.getDate();
  var mm = today.getMonth()+1; //January is 0!
  var yyyy = today.getFullYear();
  
  if(dd<10) { dd = '0'+dd }
  if(mm<10) { mm = '0'+mm  } 
  today = mm + '/' + dd + '/' + yyyy;
  
  return today;
};


// -------------------------------------------------------------------------------------


export const exampleData = {

  matter: {
    "app_num": "15/884,435",
    "applicant": "NEATFREAK GROUP INC.",
    "art_unit": "3736",
    "conf_num": "2929",
    "due_date": "02/17/2017",
    "examiner": "STEVENS, ALLAN D",
    "filing_date": "01/31/2018",
    "inventor": "BARRE, BERTRAND",
    "assignee": "ACME, Corp.",
    "notification_date": "06/14/2018",
    "num": "5885-077",
    "pub_date": "08/24/2017",
    "pub_num": "US-2017-0244662",
    "issue_fee": "$960",
    "issue_date": "10/22/2022",
    "patent_num": "9504438",
    "title": "TOTABLE CONTAINER WITH ENHANCED CAPACITY LID"
  },

  issues: [
    {
      "basis": "missing.fees",
      "claims": "2940",
      "confidence": 1.0,
      "line_num": 2,
      "page_num": 2,
      "references": [],
      "rtxt": "statement in compliance with 37 CFR 1.64, executed by or with respect to each actual inventor no later than the oath or declaration, the applicant must file an oath or declaration in compliance with 37 CFR 1.63, or a substitute reply is set forth in this notice for correction of these deficiencies. However, if a deficiency relates to the inventor's Applicant is notified that the above-identified application contains the deficiencies noted below. No period for Items Required To Avoid Processing Delays: \u2022$ 2940 TOTAL FEE BALANCE DUE. "
    },
    {
      "basis": "missing.dec",
      "claims": "Christopher H. Claudatos, William Dale Andruss",
      "confidence": 1.0,
      "line_num": 9,
      "page_num": 2,
      "references": [],
      "rtxt": "\u2022 A properly executed inventor's oath or declaration has not been received for the following inventor(s): Christopher H. Claudatos William Dale Andruss Replies must be received in the USPTO within the set time period or must include a proper Certificate of Mailing or Transmission under 37 CFR 1.8 with a mailing or transmission date within the set time period. For more information and a suggested format, see Form PTO/SB/92 and MPEP 512. "
    },
    {
      "basis": "obj.draw",
      "claims": "",
      "confidence": 1.0,
      "line_num": 39,
      "page_num": 2,
      "references": [],
      "rtxt": "11) The drawing(s) filed on 31 January 2018 is/are: a) accepted or b) objected to by the Examiner. Applicant may not request that any objection to the drawing(s) be held in abeyance. See 37 CFR 1.85(a). Replacement drawing sheet(s) including the correction is required if the drawing (s) is objected to. See 37 CFR 1.121 (d). Priority under 35 U.S.C. \u00a7 119 12) Acknowledgment is made of a claim for foreign priority under 35 U.S.C. \u00a7 119(a)-(d) or (f). "
    },
    {
      "basis": "obj.draw",
      "claims": "",
      "confidence": 1.0,
      "line_num": 41,
      "page_num": 2,
      "references": [],
      "rtxt": "Replacement drawing sheet(s) including the correction is required if the drawing (s) is objected to. See 37 CFR 1.121 (d). Priority under 35 U.S.C. \u00a7 119 12) Acknowledgment is made of a claim for foreign priority under 35 U.S.C. \u00a7 119(a)-(d) or (f). Certified copies: a) All b) Some** c) None of the: 1. Certified copies of the priority documents have been received. "
    },
    {
      "basis": "obj.spec",
      "claims": "",
      "confidence": 1.0,
      "line_num": 27,
      "page_num": 1,
      "references": [],
      "rtxt": "scanned or properly stored because: CFR 1.125(b)). The specification, claims, and/or abstract page(s) submitted is not acceptable and cannot be set forth in 37 CFR 1.125(c) and a statement that the substitute specification contains no new matter (see 37 specification must be submitted with markings and be accompanied by a clean version (without markings) as \u25cf A substitute specification in compliance with 37 CFR 1.52, 1.121(b)(3), and 1.125, is required. The substitute "
    },
    {
      "basis": "obj.draw",
      "claims": "",
      "confidence": 1.0,
      "line_num": 35,
      "page_num": 1,
      "references": [],
      "rtxt": "\u2022 Replacement drawings in compliance with 37 CFR 1.84 and 37 CFR 1.121 (d) are required. The drawings submitted are not acceptable because: \u2022 More than one figure is present and each figure is not labeled \"Fig.\" with a consecutive Arabic numeral (1, 2, etc.) or an Arabic numeral and capital letter in the English alphabet (A, B, etc.) (see 37 CFR 1.84(u)(1)). See Figure(s) 5. A brief description of the several views of the drawings (see 37 CFR 1.74) should be added or amended to correspond to the corrected numbering of the figures. See also 37 CFR 1.77(b)(9). "
    },
    {
      "basis": "obj.spec",
      "claims": "",
      "confidence": 1.0,
      "line_num": 8,
      "page_num": 3,
      "references": [],
      "rtxt": "2. The specification is objected to as failing to provide proper antecedent basis for the claimed subject matter. See 37 CFR 1.75(d)(1) and MPEP \u00a7 608.01 (0). Correction of the following is required: \"height\", \"articulating regions\", \"first substantially triangular articulating region\", \"second substantially triangular articulating region\", \"third substantially triangular articulating region\", \"fourth substantially triangular articulating region\". "
    },
    {
      "basis": "obj.spec",
      "claims": "",
      "confidence": 1.0,
      "line_num": 14,
      "page_num": 3,
      "references": [],
      "rtxt": "3. The disclosure is objected to because of the following informalities: multiple claim limitations such as \"the height of the lid adding to the storage capacity of the body when the lid is in the open configuration\" of claim 1 are not described in the Specification. Appropriate correction is required. "
    },
    {
      "basis": "allow",
      "claims": "29-44",
      "confidence": 1,
      "line_num": 2,
      "page_num": 2,
      "references": [],
      "rtxt": "Claims 29-44 are allowable."
    },
    {
      "basis": "112p1",
      "claims": "1-5 and 7-12",
      "confidence": 1.0,
      "line_num": 5,
      "page_num": 4,
      "references": [],
      "rtxt": "5. Claims 1-21 are rejected under 35 U.S.C. 112(a) or 35 U.S.C. 112 (pre-AIA), first paragraph, as not being enabled."
    },
    {
      "basis": "112p2",
      "claims": "1-21",
      "confidence": 1.0,
      "line_num": 5,
      "page_num": 4,
      "references": [],
      "rtxt": "5. Claims 1-21 are rejected under 35 U.S.C. 112(b) or 35 U.S.C. 112 (pre-AIA), second paragraph, as being indefinite for failing to particularly point out and distinctly claim the subject matter which the inventor or a joint inventor, or for pre-AIA the applicant regards as the invention. "
    },
    {
      "basis": "101",
      "claims": "1-14 and 18-20",
      "confidence": 1.0,
      "line_num": 3,
      "page_num": 6,
      "references": [],
      "rtxt": "15. Claim(s) 1-14 and 18-20 is/are rejected under 35 U.S.C. 101 as being directed to non-statutory subject matter. "
    },
    {
      "basis": "102",
      "claims": "1-14 and 18-20",
      "confidence": 1.0,
      "line_num": 3,
      "page_num": 6,
      "references": [
        {
          "alias": "Sefton",
          "country": "US",
          "num": "440656",
          "type": "pat"
        }
      ],
      "rtxt": "15. Claim(s) 1-14 and 18-20 is/are rejected under 35 U.S.C. 102(a)(1) as being anticipated by Sefton (US 440656). "
    },
    {
      "basis": "102",
      "claims": "1-8, 13, 15, and 18-20",
      "confidence": 1.0,
      "line_num": 5,
      "page_num": 9,
      "references": [
        {
          "alias": "Fontana",
          "country": "US",
          "num": "2714911",
          "type": "pat"
        }
      ],
      "rtxt": "16. Claim(s) 1-8, 13, 15, and 18-20 is/are rejected under 35 U.S.C. 102(a)(1) as being anticipated by Fontana (US 2714911). "
    },
    {
      "basis": "102",
      "claims": "1 and 21",
      "confidence": 1.0,
      "line_num": 7,
      "page_num": 12,
      "references": [
        {
          "alias": "Vivian",
          "country": "US",
          "num": "2533539",
          "type": "pat"
        }
      ],
      "rtxt": "17. Claim(s) 1 and 21 is/are rejected under 35 U.S.C. 102(a)(1) as being anticipated by Vivian (US 2533539). "
    },
    {
      "basis": "103",
      "claims": "17",
      "confidence": 1.0,
      "line_num": 5,
      "page_num": 14,
      "references": [
        {
          "alias": "Fontana",
          "country": "US",
          "num": "2714911",
          "type": "pat"
        },
        {
          "alias": "Kellogg",
          "country": "US",
          "num": "6948632",
          "type": "pat"
        }
      ],
      "rtxt": "20. Claim 17 is/are rejected under 35 U.S.C. 103 as being unpatentable over Fontana (US 2714911) as applied to claim 13 above, and further in view of Kellogg (US 6948632). Claim 17: Fontana discloses the claimed invention except for at least a portion of a frame being constructed of a wire material. "
    },

    {
      "basis": "other",
      "claims": "Here are some notes",
      "confidence": 1,
      "line_num": 8,
      "page_num": 3,
      "references": [],
      "rtxt": "2. The specification is objected to as failing to provide proper antecedent basis for the claimed subject matter. See 37 CFR 1.75(d)(1) and MPEP \u00a7 608.01 (0). Correction of the following is required: \"height\", \"articulating regions\", \"first substantially triangular articulating region\", \"second substantially triangular articulating region\", \"third substantially triangular articulating region\", \"fourth substantially triangular articulating region\". "
    },
    
  ]
};







